import { useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import FileInfo from "files/types/interfaces/fileInfo";
import { Loader2 } from "lucide-react";
import { Button } from "~/components/ui/button";
import { useStepperContext } from "~/components/ui/stepper";
import { cn } from "~/lib/utils";

const { VITE_CATALOG_API } = import.meta.env;

interface ValidationNavigationProps {
  onClose: () => void;
  onFileValidated: (file: FileInfo, validationMetadata: any) => void;
}

export const ValidationNavigation = ({ onClose, onFileValidated }: ValidationNavigationProps) => {
  const { datasetId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { currentStep, setCurrentStep, stepperState } = useStepperContext();

  const [isStartingIngestion, setIsStartingIngestion] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Get ingestion ID from URL if it exists
  const ingestionIdFromUrl = searchParams.get("ingestion");
  const hasExistingIngestion = !!ingestionIdFromUrl;

  // Get selected file from stepper state
  const selectedFile = stepperState?.uploadState?.selectedFile;

  /**
   * Start an ingestion record (NOT jobs yet).
   * This creates a DB entry for tracking the validation process.
   *
   * @param platformStorageKey - The file key/path in platform storage (e.g., "org/path/to/file.csv")
   */
  const startIngestion = async (platformStorageKey: string): Promise<string | null> => {
    if (!datasetId) {
      setError("Dataset ID is required");
      return null;
    }

    if (!platformStorageKey) {
      setError("File storage key is required");
      return null;
    }

    setIsStartingIngestion(true);
    setError(null);

    try {
      const response = await fetch(`${VITE_CATALOG_API}/datasets/${datasetId}/ingestions`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          platform_storage_key: platformStorageKey,
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to create ingestion: ${errorText}`);
      }

      // Backend returns plain text with quotes, need to strip them
      let ingestionId = await response.text();
      ingestionId = ingestionId.replace(/^"|"$/g, ""); // Remove leading/trailing quotes

      return ingestionId;
    } catch (err: any) {
      const errorMsg = err.message || "Failed to start ingestion. Please try again.";
      console.error("Failed to start ingestion:", err);
      setError(errorMsg);
      return null;
    } finally {
      setIsStartingIngestion(false);
    }
  };

  /**
   * Handle the primary action button click
   */
  const handleNext = async () => {
    if (currentStep === 0) {
      // Step 0: File Selection
      if (hasExistingIngestion) {
        // Already have an ingestion, just move to next step
        setCurrentStep(1);
      } else {
        // No existing ingestion - create one
        if (!selectedFile) {
          setError("Please select a file first");
          return;
        }

        // Use the file key as the platform storage key
        const platformStorageKey = selectedFile.key;
        const ingestionId = await startIngestion(platformStorageKey);
        if (ingestionId) {
          // Update URL with ingestion ID
          const newParams = new URLSearchParams(searchParams);
          newParams.set("ingestion", ingestionId);
          setSearchParams(newParams, { replace: true });

          // Move to next step
          setCurrentStep(1);
        }
      }
    } else if (currentStep === 1) {
      // Step 1: Validation - move to summary
      setCurrentStep(2);
    } else if (currentStep === 2) {
      // Step 2: Summary - complete and close
      const mockValidationMetadata = {
        totalRows: 1234,
        dataRows: 1233,
        columns: 8,
        headerRow: 1,
      };
      onFileValidated(selectedFile || ({} as FileInfo), mockValidationMetadata);
      onClose();
    }
  };

  /**
   * Handle back navigation
   */
  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
      setError(null);
    }
  };

  /**
   * Handle modal close with URL cleanup
   */
  const handleClose = () => {
    // Clear ingestion ID from URL
    const newParams = new URLSearchParams(searchParams);
    newParams.delete("ingestion");
    setSearchParams(newParams, { replace: true });
    onClose();
  };

  /**
   * Determine if the "Next" button should be enabled
   */
  const canProceed = (): boolean => {
    if (currentStep === 0) {
      // Can proceed if we have a file selected OR an existing ingestion
      return !!selectedFile || hasExistingIngestion;
    }
    return true;
  };

  /**
   * Get the label for the primary action button
   */
  const getNextButtonLabel = (): string => {
    if (currentStep === 0) {
      if (hasExistingIngestion) {
        return "Continue to Validation";
      }
      return "Start Validation";
    } else if (currentStep === 1) {
      return "Continue";
    } else if (currentStep === 2) {
      return "Confirm & Submit";
    }
    return "Next";
  };

  /**
   * Get the label for the back/cancel button
   */
  const getBackButtonLabel = (): string => {
    switch (currentStep) {
      case 1:
      case 2:
        return "Previous";
      default:
        return "Cancel";
    }
  };

  return (
    <div className="space-y-3">
      {/* Error display */}
      {error && (
        <div
          className={cn("p-3 rounded-lg text-sm border", "bg-red-50 border-red-200 text-red-700")}
        >
          {error}
        </div>
      )}

      {/* Info banner when resuming existing ingestion */}
      {hasExistingIngestion && currentStep === 0 && (
        <div
          className={cn(
            "p-3 rounded-lg text-sm border",
            "bg-blue-50 border-blue-200 text-blue-700"
          )}
        >
          <p className="font-medium">Existing validation found</p>
          <p className="text-xs mt-1">
            You have an in-progress validation. Click &ldquo;Continue to Validation&rdquo; to
            resume.
          </p>
        </div>
      )}

      {/* Navigation buttons */}
      <div className="flex justify-end gap-3 pt-6 border-t">
        {currentStep > 0 ? (
          <Button variant="outline" onClick={handleBack} disabled={isStartingIngestion}>
            {getBackButtonLabel()}
          </Button>
        ) : (
          <Button variant="outline" onClick={handleClose} disabled={isStartingIngestion}>
            Cancel
          </Button>
        )}

        <Button onClick={handleNext} disabled={!canProceed() || isStartingIngestion}>
          {isStartingIngestion ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Starting...
            </>
          ) : (
            getNextButtonLabel()
          )}
        </Button>
      </div>
    </div>
  );
};
